import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Alert } from 'react-bootstrap';
import { FiDownload, FiExternalLink, FiX, FiMaximize2, FiMinimize2 } from 'react-icons/fi';

interface PdfViewerProps {
  url: string | null;
  title?: string;
  show: boolean;
  onHide: () => void;
  loading?: boolean;
  error?: string | null;
}

/**
 * PdfViewer component for displaying PDF documents in a modal.
 * Uses an iframe to render the PDF with browser's native PDF viewer.
 */
const PdfViewer: React.FC<PdfViewerProps> = ({
  url,
  title = 'Documento PDF',
  show,
  onHide,
  loading = false,
  error = null,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);

  // Reset iframe loading state when URL changes
  useEffect(() => {
    if (url) {
      setIframeLoading(true);
    }
  }, [url]);

  const handleIframeLoad = () => {
    setIframeLoading(false);
  };

  const handleDownload = () => {
    if (url) {
      // Open in new tab for download
      window.open(url, '_blank');
    }
  };

  const handleOpenExternal = () => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      fullscreen={isFullscreen ? true : undefined}
      centered
      className="pdf-viewer-modal"
    >
      <Modal.Header className="border-bottom">
        <Modal.Title className="d-flex align-items-center gap-2">
          <span>{title}</span>
        </Modal.Title>
        <div className="d-flex align-items-center gap-2 ms-auto">
          {url && !loading && !error && (
            <>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleDownload}
                title="Descargar PDF"
              >
                <FiDownload />
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleOpenExternal}
                title="Abrir en nueva pestaña"
              >
                <FiExternalLink />
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={toggleFullscreen}
                title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
              >
                {isFullscreen ? <FiMinimize2 /> : <FiMaximize2 />}
              </Button>
            </>
          )}
          <Button
            variant="link"
            className="text-muted p-0"
            onClick={onHide}
            title="Cerrar"
          >
            <FiX size={20} />
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body className="p-0" style={{ minHeight: isFullscreen ? 'calc(100vh - 60px)' : '70vh' }}>
        {loading && (
          <div className="d-flex flex-column align-items-center justify-content-center h-100 py-5">
            <Spinner animation="border" variant="primary" />
            <p className="text-muted mt-3">Cargando documento...</p>
          </div>
        )}

        {error && (
          <div className="d-flex align-items-center justify-content-center h-100 p-4">
            <Alert variant="danger" className="text-center">
              <Alert.Heading>Error al cargar el PDF</Alert.Heading>
              <p>{error}</p>
              <Button variant="outline-danger" onClick={onHide}>
                Cerrar
              </Button>
            </Alert>
          </div>
        )}

        {url && !loading && !error && (
          <div className="position-relative h-100">
            {iframeLoading && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-light"
                style={{ zIndex: 1 }}
              >
                <Spinner animation="border" variant="primary" />
              </div>
            )}
            <iframe
              src={url}
              title={title}
              width="100%"
              height="100%"
              style={{
                border: 'none',
                minHeight: isFullscreen ? 'calc(100vh - 60px)' : '70vh',
              }}
              onLoad={handleIframeLoad}
            />
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default PdfViewer;
